import React, { memo, useCallback, useState } from 'react';
import EditInitialSignupModal from './EditInitialSignupModal';
import { AdminTable, StatusBadge, IconButton } from 'components/admin';
import { ColumnDef } from 'types/admin';

export interface InitialSignupConfigAPI {
    startDate: string;
    endDate: string;
    id: number;
    configName: string;
    creditAmount: number;
   
    promoLimit: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    configurationType: string;
    airlineIds?: number[];
    airlineNames?: string[];
}

export interface InitialSignupUpdatePayload {
    id: number;
    creditAmount: number;
    configName: string;
    configurationType: string;
   
    promoLimit: number;
    isActive: boolean;
    startDate: string;
    endDate: string;
}

interface InitialSignupTableProps {
    configurations: InitialSignupConfigAPI[];
    onDelete: (id: number, name:string) => void;
    loading: boolean;
    onUpdate: (updatedConfig: InitialSignupUpdatePayload) => void; 
    refreshTable?: () => void;
}

const InitialSignupTable: React.FC<InitialSignupTableProps> = memo(({ configurations, onDelete, loading, onUpdate }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<InitialSignupConfigAPI | null>(null);

    const openModal = useCallback((config: InitialSignupConfigAPI) => {
        setSelectedConfig(config);
        setIsModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedConfig(null);
    }, []);

    
    const handleUpdate = useCallback(async (updatedConfigData: InitialSignupUpdatePayload) => {
        if (!selectedConfig) return;
      
        try {
          await onUpdate(updatedConfigData); // ✅ Wait for the async update
          closeModal(); // ✅ Only close modal after success
        } catch (error) {
          console.error('Update failed:', error);
        }
      }, [onUpdate, closeModal, selectedConfig]);

    // Define table columns
    const columns: ColumnDef<InitialSignupConfigAPI>[] = [
        {
            key: 'configName',
            header: 'Configuration Name',
        },
        {
            key: 'createdAt',
            header: 'Created Date',
            render: (config) => new Date(config.createdAt).toLocaleDateString('en-GB'),
        },
        {
            key: 'isActive',
            header: 'Status',
            render: (config) => <StatusBadge status={config.isActive} />,
        },
    ];

    // Custom actions renderer
    const renderActions = (config: InitialSignupConfigAPI) => (
        <>
            <IconButton
                variant="edit"
                onClick={() => openModal(config)}
                title="Edit"
                ariaLabel="Edit initial signup configuration"
            />
            <IconButton
                variant="delete"
                onClick={() => onDelete(config.id, config.configName)}
                title="Delete"
                ariaLabel="Delete initial signup configuration"
            />
        </>
    );

    return (
        <>
            <AdminTable
                columns={columns}
                data={configurations}
                loading={loading}
                emptyMessage="No initial signup configurations found"
                renderActions={renderActions}
                showSerialNumber={true}
            />

{selectedConfig && (
    <EditInitialSignupModal
                    isOpen={isModalOpen}
                    config={{
                        id: selectedConfig.id,
                        configName: selectedConfig.configName,
                        configurationType: selectedConfig.configurationType,
                        creditAmount: selectedConfig.creditAmount,
                        
                        promoLimit: selectedConfig.promoLimit,
                        isActive: selectedConfig.isActive,
                        startDate: selectedConfig.startDate,
                        endDate: selectedConfig.endDate,
                        airlineIds: selectedConfig.airlineIds ||  [],
                        airlineNames: selectedConfig.airlineNames || []
                    }}
                    onUpdate={handleUpdate} 
                    onClose={closeModal}  
                      />
)}
        </>
    );
});

InitialSignupTable.displayName = 'InitialSignupTable';

export default InitialSignupTable;
